const { VITE_API_URL: API, VITE_AUTH_SCOPE: SCOPE } = import.meta.env;

const jsonHeaders: HeadersInit = {
  'Content-Type': 'application/json',
  'X-Auth-Scope': SCOPE,
};

const baseInit: RequestInit = {
  credentials: 'include',
  headers: jsonHeaders,
};

export interface PresignSingleResponse {
  url: string;
  key: string;
}

export interface CreateMultipartResponse {
  uploadId: string;
  key: string;
}

export interface SignPartResponse {
  url: string;
}

export interface CompleteMultipartResponse {
  location?: string;
}

export interface S3Part {
  PartNumber?: number;
  ETag?: string;
}

export async function presignSingle(
  filename: string,
  contentType: string,
): Promise<PresignSingleResponse> {
  const res = await fetch(`${API}/s3/presign`, {
    ...baseInit,
    method: 'POST',
    body: JSON.stringify({ filename, content_type: contentType }),
  });
  if (!res.ok) throw new Error(`presign failed: ${res.status}`);
  return res.json();
}

export async function createMultipart(
  filename: string,
  contentType: string,
): Promise<CreateMultipartResponse> {
  const res = await fetch(`${API}/s3/multipart`, {
    ...baseInit,
    method: 'POST',
    body: JSON.stringify({ filename, content_type: contentType }),
  });
  if (!res.ok) throw new Error(`createMultipart failed: ${res.status}`);
  const data = (await res.json()) as { upload_id: string; key: string };
  return { uploadId: data.upload_id, key: data.key };
}

export async function signPart(
  uploadId: string,
  key: string,
  partNumber: number,
): Promise<SignPartResponse> {
  const url = `${API}/s3/multipart/${uploadId}/presign/${partNumber}?key=${encodeURIComponent(key)}`;
  const res = await fetch(url, baseInit);
  if (!res.ok) throw new Error(`signPart failed: ${res.status}`);
  return res.json();
}

export async function completeMultipart(
  uploadId: string,
  key: string,
  parts: S3Part[],
): Promise<CompleteMultipartResponse> {
  // Uppy types PartNumber/ETag as optional, but S3 rejects a completion that is
  // missing either on any part. Fail loudly here rather than POSTing nulls that
  // the backend would 422 on with a less obvious error.
  const normalizedParts = parts.map((p, index) => {
    if (p.PartNumber == null || p.ETag == null) {
      throw new Error(
        `completeMultipart: part #${index} is missing ${
          p.PartNumber == null ? 'PartNumber' : 'ETag'
        }`,
      );
    }
    return { part_number: p.PartNumber, etag: p.ETag };
  });
  const res = await fetch(`${API}/s3/multipart/${uploadId}/complete`, {
    ...baseInit,
    method: 'POST',
    body: JSON.stringify({ key, parts: normalizedParts }),
  });
  if (!res.ok) throw new Error(`completeMultipart failed: ${res.status}`);
  return res.json();
}

export async function abortMultipart(
  uploadId: string,
  key: string,
): Promise<void> {
  await fetch(
    `${API}/s3/multipart/${uploadId}?key=${encodeURIComponent(key)}`,
    { ...baseInit, method: 'DELETE' },
  );
}
